/**
 * Tiny line-level diff using longest common subsequence (LCS).
 *
 * Returns an aligned list of pairs where each row is either:
 *   - `equal`: same line on both sides
 *   - `del`  : a line only on the left (removed)
 *   - `add`  : a line only on the right (added)
 *
 * Good enough for the few-kB prompt markdowns we diff in the Skill editor.
 * Don't reach for it on multi-MB inputs — the matrix is O(N·M).
 */

export interface DiffRow {
  kind: 'equal' | 'del' | 'add';
  left: string | null;
  right: string | null;
  leftLine: number | null;
  rightLine: number | null;
}

export function lineDiff(before: string, after: string): DiffRow[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;

  // LCS lengths matrix. Pre-fill `0`s so indexing is total — paired with
  // `noUncheckedIndexedAccess` we then need explicit `?? 0` reads anyway.
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const row = dp[i] as number[];
      const nextRow = dp[i + 1] as number[];
      if (a[i] === b[j]) row[j] = (nextRow[j + 1] ?? 0) + 1;
      else row[j] = Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0);
    }
  }

  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const ai = a[i] ?? '';
    const bj = b[j] ?? '';
    const rowI = dp[i] as number[];
    const rowI1 = dp[i + 1] as number[];
    if (ai === bj) {
      rows.push({ kind: 'equal', left: ai, right: bj, leftLine: i + 1, rightLine: j + 1 });
      i++;
      j++;
    } else if ((rowI1[j] ?? 0) >= (rowI[j + 1] ?? 0)) {
      rows.push({ kind: 'del', left: ai, right: null, leftLine: i + 1, rightLine: null });
      i++;
    } else {
      rows.push({ kind: 'add', left: null, right: bj, leftLine: null, rightLine: j + 1 });
      j++;
    }
  }
  while (i < n) {
    rows.push({ kind: 'del', left: a[i] ?? '', right: null, leftLine: i + 1, rightLine: null });
    i++;
  }
  while (j < m) {
    rows.push({ kind: 'add', left: null, right: b[j] ?? '', leftLine: null, rightLine: j + 1 });
    j++;
  }
  return rows;
}

export function diffStats(rows: DiffRow[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const r of rows) {
    if (r.kind === 'add') added++;
    else if (r.kind === 'del') removed++;
  }
  return { added, removed };
}
